import { usePathname, useRouter } from "next/navigation";

import { useUser } from "@clerk/nextjs";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useTRPC } from "@/frontend/lib/trpc";

// QUERIES
export function useJamQuery(id: string) {
  const { user } = useUser();
  const pathname = usePathname();
  const trpc = useTRPC();
  const isSharedView = pathname?.startsWith("/share");

  return useQuery({
    ...trpc.jam.get.queryOptions({ id }),
    enabled: !!user || isSharedView,
    retry: 3,
    retryDelay: (attemptIndex) =>
      Math.min(100 * Math.pow(2, attemptIndex), 1000),
    refetchInterval: 30 * 1000,
    staleTime: 0,
  });
}

export function useJamsQuery(limit = 20) {
  const { user } = useUser();
  const trpc = useTRPC();

  return useInfiniteQuery({
    ...trpc.jam.list.infiniteQueryOptions(
      {
        limit,
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          if (!lastPage.hasMore) return undefined;
          return allPages.length * limit;
        },
      },
    ),
    initialPageParam: 0,
    refetchInterval: 30 * 1000,
    enabled: !!user,
  });
}

export function useShareJamQuery(shareId: string) {
  const pathname = usePathname();
  const trpc = useTRPC();

  const isSharedView = pathname?.startsWith("/share");

  return useQuery({
    ...trpc.jam.getPublic.queryOptions({ shareId }),
    enabled: isSharedView,
    retry: 3,
    retryDelay: (attemptIndex) =>
      Math.min(100 * Math.pow(2, attemptIndex), 1000),
    refetchInterval: 30 * 1000,
    staleTime: 0,
  });
}

// MUTATIONS
export function useCreateJam() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    ...trpc.jam.create.mutationOptions(),
    onMutate: async ({
      prompt,
      jamId,
      messageId,
      projectId,
      uploadedFiles = [],
      selectedRepos = [],
      twitterUsernames = [],
    }) => {
      // Generate IDs if not provided
      const tempJamId = jamId || crypto.randomUUID();
      const tempMessageId = messageId || crypto.randomUUID();

      // Cancel any outgoing refetches
      await queryClient.cancelQueries(
        trpc.jam.get.queryFilter({ id: tempJamId }),
      );
      await queryClient.cancelQueries(
        trpc.event.list.queryFilter({ jamId: tempJamId }),
      );

      const optimisticJam = {
        id: tempJamId,
        userId: "", // Will be filled by server
        runId: null,
        runAccessToken: null,
        instanceId: null,
        snapshotId: null,
        status: "starting" as const,
        error: null,
        isPublic: false,
        shareId: null,
        title: null,
        lastEventId: tempMessageId,
        projectId: projectId || null,
        deleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        githubRepo: null,
        _deprecated_effortLevel: "",
        _deprecated_tasks: null,
        _deprecated_stopTaskRunId: null,
      };

      queryClient.setQueryData(
        trpc.jam.get.queryKey({ id: tempJamId }),
        optimisticJam,
      );

      queryClient.setQueryData(trpc.event.list.queryKey({ jamId: tempJamId }), [
        {
          id: tempMessageId,
          date: new Date(),
          type: "user" as const,
          text: prompt,
          files:
            uploadedFiles.length > 0
              ? uploadedFiles.map((file) => ({
                  name: file.name,
                  type: file.type,
                  size: file.size,
                }))
              : undefined,
          repos: selectedRepos.length > 0 ? selectedRepos : undefined,
          twitterUsernames:
            twitterUsernames.length > 0 ? twitterUsernames : undefined,
        },
      ]);

      queryClient.setQueryData(trpc.jam.list.infiniteQueryKey(), (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: [
            ...old.pages,
            {
              jams: [...old.pages[0].jams, optimisticJam],
              total: old.pages[0].total + 1,
              hasMore: true,
            },
          ],
        };
      });

      // Navigate to the jam
      setTimeout(() => {
        router.push(`/jam/${tempJamId}`);
      }, 0);

      return undefined;
    },
    onSuccess: async (result) => {
      await queryClient.cancelQueries(
        trpc.jam.get.queryFilter({ id: result.id }),
      );
      queryClient.setQueryData(
        trpc.jam.get.queryKey({ id: result.id }),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            status: "acting" as const,
            runId: result.runId,
            runAccessToken: result.accessToken,
          };
        },
      );
      await queryClient.invalidateQueries(
        trpc.jam.get.queryFilter({ id: result.id }),
      );
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries(trpc.jam.list.infiniteQueryFilter());
      await queryClient.invalidateQueries(
        trpc.project.getJams.queryFilter(variables.projectId),
      );
    },
  });
}

export function useContinueJam() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.jam.continue.mutationOptions(),
    onMutate: async ({
      id,
      prompt,
      messageId,
      uploadedFiles = [],
      selectedRepos = [],
      twitterUsernames = [],
    }) => {
      await queryClient.cancelQueries(trpc.jam.get.queryFilter({ id }));
      await queryClient.cancelQueries(
        trpc.event.list.queryFilter({ jamId: id }),
      );

      queryClient.setQueryData(trpc.jam.get.queryKey({ id }), (old) => {
        if (!old) return old;
        return {
          ...old,
          status: "continuing" as const,
          error: null,
        };
      });

      queryClient.setQueryData(
        trpc.event.list.queryKey({ jamId: id }),
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return [
            ...old,
            {
              id: messageId,
              date: new Date(),
              type: "user" as const,
              text: prompt,
              files:
                uploadedFiles.length > 0
                  ? uploadedFiles.map((file) => ({
                      name: file.name,
                      type: file.type,
                      size: file.size,
                    }))
                  : undefined,
              repos: selectedRepos.length > 0 ? selectedRepos : undefined,
              twitterUsernames:
                twitterUsernames.length > 0 ? twitterUsernames : undefined,
            },
          ];
        },
      );

      queryClient.setQueryData(trpc.jam.list.infiniteQueryKey(), (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            jams: page.jams.map((jam) =>
              jam.id === id ? { ...jam, status: "continuing" as const } : jam,
            ),
          })),
        };
      });

      return undefined;
    },
    onSuccess: async (result) => {
      await queryClient.cancelQueries(
        trpc.jam.get.queryFilter({ id: result.id }),
      );
      queryClient.setQueryData(
        trpc.jam.get.queryKey({ id: result.id }),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            status: "acting" as const,
            runId: result.runId,
            runAccessToken: result.accessToken,
          };
        },
      );
      await queryClient.invalidateQueries(
        trpc.jam.get.queryFilter({ id: result.id }),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(trpc.jam.list.infiniteQueryFilter());
    },
  });
}

export function useStopJam() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.jam.stop.mutationOptions(),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(trpc.jam.get.queryFilter({ id }));
      await queryClient.cancelQueries(
        trpc.event.list.queryFilter({ jamId: id }),
      );

      queryClient.setQueryData(trpc.jam.get.queryKey({ id }), (old) => {
        if (!old) return old;
        return {
          ...old,
          status: "idle" as const,
          error: null,
        };
      });

      queryClient.setQueryData(
        trpc.event.list.queryKey({ jamId: id }),
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.slice(0, -1);
        },
      );

      queryClient.setQueryData(trpc.jam.list.infiniteQueryKey(), (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            jams: page.jams.map((jam) =>
              jam.id === id ? { ...jam, status: "idle" as const } : jam,
            ),
          })),
        };
      });

      return undefined;
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries(
        trpc.jam.get.queryFilter({ id: variables.id }),
      );
      await queryClient.invalidateQueries(
        trpc.event.list.queryFilter({ jamId: variables.id }),
      );
      await queryClient.invalidateQueries(trpc.jam.list.infiniteQueryFilter());
    },
  });
}

export function useDeleteJam() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.jam.delete.mutationOptions(),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(trpc.jam.list.infiniteQueryFilter());

      queryClient.setQueryData(trpc.jam.list.infiniteQueryKey(), (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            jams: page.jams.filter((jam) => jam.id !== id),
            total: page.total - 1,
          })),
        };
      });

      return undefined;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(trpc.jam.list.infiniteQueryFilter());
      await queryClient.invalidateQueries(trpc.project.getJams.queryFilter());
    },
  });
}

export function useToggleJamVisibility() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.jam.toggleVisibility.mutationOptions(),
    onMutate: async ({ id, isPublic }) => {
      await queryClient.cancelQueries(trpc.jam.get.queryFilter({ id }));

      queryClient.setQueryData(trpc.jam.get.queryKey({ id }), (old) => {
        if (!old) return old;
        return {
          ...old,
          isPublic,
        };
      });

      return undefined;
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries(
        trpc.jam.get.queryFilter({ id: variables.id }),
      );
    },
  });
}
